import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';

import { useAuthHelper } from '../components/AuthContextProvider';
import { AuthenticationThunks } from '../actions/thunks/authentication-thunks';

export const OktaAuthBridge: React.FC = () => {
    const { authState } = useOktaAuth();
    const authHelper = useAuthHelper();
    const dispatch = useDispatch();
    const lastAttemptedAccessToken = React.useRef<string | null>(null);
    const loginSyncInFlight = React.useRef(false);

    React.useEffect(() => {
        if (!authState) return;

        if (authState.isAuthenticated) {
            const oktaAccessToken = authState.accessToken?.accessToken;

            // Fire /login once per Okta access token. Okta never silently renews the
            // access token (autoRenew is off and we never write tokens back to it), so
            // this runs exactly once per Okta authentication (initial login or a
            // session restore on relaunch) and does not loop.
            if (!oktaAccessToken || loginSyncInFlight.current || lastAttemptedAccessToken.current === oktaAccessToken) {
                return;
            }

            loginSyncInFlight.current = true;
            lastAttemptedAccessToken.current = oktaAccessToken;

            const runLogin = async (): Promise<void> => {
                try {
                    await (dispatch(
                        AuthenticationThunks.login(oktaAccessToken, (args) => {
                            authHelper.onUserAuthenticated(args);
                        })
                    ) as unknown as Promise<void>);
                } finally {
                    loginSyncInFlight.current = false;
                }
            };
            runLogin();
            return;
        }
    }, [authState, authHelper, dispatch]);

    return null;
};

